import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Hammer, Loader2, Rocket, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: "em produção", label: "Em produção", icon: Hammer },
  { id: "em revisão", label: "Em revisão", icon: Search },
  { id: "entregue", label: "Entregue", icon: Rocket },
];

export const ProjectTracker = () => {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    fetch(`${API}/projects/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setProjects(d.projects || []))
      .catch(() => setProjects([]));
  }, []);

  return (
    <div className="mt-6 rounded-2xl border border-[#22222A] bg-[#101014]">
      <div className="flex items-center gap-3 border-b border-[#1A1A20] px-8 py-5">
        <Rocket className="h-5 w-5 text-[#E50914]" />
        <div>
          <h2 className="font-display text-lg font-bold">Acompanhar meu projeto</h2>
          <p className="text-xs text-zinc-500">O andamento do seu site em tempo real</p>
        </div>
      </div>

      <div className="space-y-6 p-6 sm:p-8" data-testid="project-tracker-list">
        {projects === null ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#E50914]" />
          </div>
        ) : projects.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-600">
            Nenhum projeto em andamento. Quando o Luiz começar seu site, o acompanhamento aparece aqui.
          </p>
        ) : (
          projects.map((p) => {
            const currentIdx = STEPS.findIndex((s) => s.id === p.status);
            return (
              <div key={p.project_id} data-testid={`project-tracker-${p.project_id.slice(-6)}`} className="rounded-xl border border-[#1A1A20] bg-[#0A0A0C] p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-display text-lg font-bold text-zinc-100">{p.title}</h3>
                  {p.status === "entregue" ? (
                    <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Entregue
                    </span>
                  ) : (
                    <span className="rounded-full border border-[#E50914]/40 bg-[#E50914]/10 px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-[#FF2A3B]">
                      {p.status}
                    </span>
                  )}
                </div>
                {p.description && <p className="mt-2 text-sm text-zinc-500">{p.description}</p>}

                <div className="mt-8 flex items-center">
                  {STEPS.map((step, i) => {
                    const reached = i <= currentIdx;
                    const isCurrent = i === currentIdx;
                    return (
                      <div key={step.id} className="flex flex-1 items-center last:flex-none">
                        <div className="flex flex-col items-center" data-testid={`project-step-${i}-${reached ? "on" : "off"}`}>
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                              isCurrent
                                ? "border-[#E50914] bg-[#E50914] text-white shadow-[0_0_20px_rgba(229,9,20,0.5)]"
                                : reached
                                  ? "border-[#E50914]/60 bg-[#E50914]/15 text-[#E50914]"
                                  : "border-[#2A2A33] bg-[#101014] text-zinc-600"
                            }`}
                          >
                            <step.icon className="h-4 w-4" />
                          </div>
                          <span className={`mt-2 text-center font-mono-code text-[10px] uppercase tracking-wider ${reached ? "text-zinc-200" : "text-zinc-600"}`}>
                            {step.label}
                          </span>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`mx-2 mb-6 h-0.5 flex-1 rounded ${i < currentIdx ? "bg-[#E50914]" : "bg-[#22222A]"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {p.status === "entregue" && p.site_url && (
                  <a
                    data-testid={`project-site-link-${p.project_id.slice(-6)}`}
                    href={p.site_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-red-glow mt-6 inline-flex items-center gap-2 rounded-full bg-[#E50914] px-6 py-3 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Ver meu site
                  </a>
                )}
                {p.status === "em revisão" && (
                  <p className="mt-6 text-sm text-zinc-500">
                    Seu site está em revisão final — em breve você recebe o link.
                  </p>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
